import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress,
  Alert,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  FormControlLabel,
  FormHelperText,
  Checkbox,
} from "@mui/material";
import { useLazyGetProductsQuery } from "../../../../Services/ProductService";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { showSnackbar } from "../../../../Redux/Slices/NotificationSlice";
import { useGetAllCountriesQuery } from "../../../../Services/CountryTaxService";
import {
  useCreateAndUpdateTaxProfileMutation,
  useGetTaxProfileDetailsQuery,
} from "../../../../Services/Taxation";
import Breadcrumb from "../../../../Components/BreadCrumb";
import Utils from "../../../../Helpers/Utils";
// added by eshwar
import { FiSearch as SearchIcon } from "react-icons/fi";
import { InputAdornment } from "@mui/material";
import { useLazyGetCountryDialingListQuery } from "../../../../Services/CountryService";
import { FaSearch } from "react-icons/fa";
// import { FaXmark } from 'react-icons/fa';

interface TaxationFormInputs {
  profileName: string;
  profileCode: string;
  isActive: boolean;
  isDefault: boolean;
  taxDetails: TaxDetail[];
  products: string[];
}

interface TaxDetail {
  _id?: string;
  // countryTaxId: string;
  countryCode: string;
  taxType: string;
  taxRate: number;
}

const AddTaxation: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: countriesData = [] } = useGetAllCountriesQuery({});
  const [getAllCountryTrigger] = useLazyGetCountryDialingListQuery();
  const [
    trigger,
    {
      data: productData,
      error,
      reset: resetProducts,
      isLoading: isLoadingProduct,
      isFetching,
    },
  ] = useLazyGetProductsQuery();
  // added by Eshwar
  const countriesDialingData: any = useSelector(
    (state: any) => state.SettingSlice.countryDialingList,
  );

  useEffect(() => {
    if (countriesDialingData?.length === 0) {
      getAllCountryTrigger();
    }
  }, [countriesDialingData]);

  // added by Eshwar
  const [countrySearch, setCountrySearch] = useState<{ [key: number]: string }>(
    {},
  );
  const [dropdownOpen, setDropdownOpen] = useState<{ [key: number]: boolean }>(
    {},
  );

  // added by Eshwar
  const filteredCountries = (index: number) => {
    const search = countrySearch[index] || "";
    const selectedCountries = fields
      .map((field, i) => (i !== index ? field.countryCode : null))
      .filter(Boolean);

    return (
      countriesDialingData?.filter(
        (country: any) =>
          country.label.toLowerCase().includes(search.toLowerCase()) &&
          !selectedCountries.includes(country.value),
      ) || []
    );
  };

  const [createAndUpdateTax, { isLoading }] =
    useCreateAndUpdateTaxProfileMutation();
  const { id } = useParams();
  const { data } = useGetTaxProfileDetailsQuery(id ?? "", {
    skip: !id,
  });
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const [productList, setProductList] = useState<any[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [taxCoverageError, setTaxCoverageError] = useState<string>("");

  // Load all products once on mount
  useEffect(() => {
    trigger({
      page: 1,
      pageSize: 9999,
      filters: [{ key: "isDelete", value: false }],
      sortBy: "productName",
      sortOrder: "asc",
    });
  }, []);

  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (productData?.data) {
      let filtered = productData.data;
      if (searchQuery.trim()) {
        const regex = new RegExp(searchQuery, "i");
        filtered = filtered.filter((p: any) => regex.test(p.productName));
      }
      // Show selected products first
      const selected = filtered.filter((p: any) =>
        selectedProducts.includes(p.id || p._id),
      );
      const unselected = filtered.filter(
        (p: any) => !selectedProducts.includes(p.id || p._id),
      );
      setProductList([...selected, ...unselected]);
    }
  }, [productData, searchQuery, selectedProducts]);

  const handleProductSelect = (productId: string) => {
    setSelectedProducts((prev) => [...prev, productId]);
    setValue("products", [...selectedProducts, productId]);
  };

  const handleProductDeselect = (productId: string) => {
    setSelectedProducts((prev) => prev.filter((p) => p !== productId));
    setValue(
      "products",
      selectedProducts.filter((p) => p !== productId),
    );
  };

  const {
    control,
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<TaxationFormInputs>({
    defaultValues: {
      profileName: "",
      profileCode: "",
      isActive: true,
      isDefault: false,
      taxDetails: [{ countryCode: "", taxType: "", taxRate: 0 }],
      products: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "taxDetails",
  });

  useEffect(() => {
    if (data) {
      // Reset the entire form with the new data
      reset({
        ...data,
        // Ensure taxDetails is always an array with at least one item
        taxDetails: data.taxDetails?.length
          ? data.taxDetails
          : [{ countryCode: "", taxType: "", taxRate: 0 }],
      });
      setSelectedProducts(data.products || []);
    } else {
      // Reset to default values when there's no data (new form)
      reset({
        profileName: "",
        profileCode: "",
        isActive: true,
        isDefault: false,
        taxDetails: [{ countryCode: "", taxType: "", taxRate: 0 }],
        products: [],
      });
      setSelectedProducts([]);
    }
  }, [data, reset]);

  const breadcrumbItems = [
    { label: "Tax List", path: "taxation" },
    { label: id ? "Edit Tax" : "Add Tax" },
  ];
  const addNewRow = () => {
    append({ countryCode: "", taxType: "", taxRate: 0 });
  };

  const onSubmit = async (data: TaxationFormInputs) => {
    // if(data.products.length === 0){
    //   dispatch(
    //     showSnackbar({
    //       message: "At least one product must be selected.",
    //       severity: "warning",
    //     })
    //   );
    //   return;
    // }

    // Every product assigned to this profile must have all of its selling
    // countries covered by the rates being saved here — otherwise saving
    // would silently leave that product computing 0% tax for whichever
    // countries this profile doesn't have a rate for (mirrors the backend
    // check in controllers/TaxProfileController.ts).
    const coveredCountries = new Set(
      (data.taxDetails || [])
        .map((t) => t.countryCode)
        .filter((code): code is string => Boolean(code)),
    );
    const allProducts: any[] = productData?.data || [];
    const coverageIssues: string[] = [];
    for (const productId of selectedProducts) {
      const product = allProducts.find(
        (p) => (p.id || p._id) === productId,
      );
      if (!product) continue;
      const missing = (product.countryIsoCodes || []).filter(
        (code: string) => !coveredCountries.has(code),
      );
      if (missing.length > 0) {
        coverageIssues.push(`"${product.productName}" (missing: ${missing.join(", ")})`);
      }
    }
    if (coverageIssues.length > 0) {
      const message = `This tax profile does not have a rate configured for every country the following selected products sell in: ${coverageIssues.join("; ")}. Please add rates for the missing countries, or deselect these products.`;
      setTaxCoverageError(message);
      dispatch(
        showSnackbar({
          message,
          severity: "error",
        }),
      );
      return;
    }
    setTaxCoverageError("");

    try {
      let result: any = "";
      if (id) {
        result = await createAndUpdateTax({
          id,
          data: data,
        }).unwrap();
        if (result.status === "success") {
          dispatch(
            showSnackbar({
              message: "Taxation edited successfully",
              severity: "success",
            }),
          );
          navigate("/taxation");
        }
      } else {
        result = await createAndUpdateTax({ data: data }).unwrap();
        if (result.status === "success") {
          dispatch(
            showSnackbar({
              message: "Taxation added successfully",
              severity: "success",
            }),
          );
          navigate("/taxation");
        }
      }
    } catch (err) {
      dispatch(
        showSnackbar({
          message:
            typeof err === "object" &&
            err !== null &&
            "data" in err &&
            typeof (err as any).data?.message === "string"
              ? (err as any).data.message
              : "Error adding taxation",
          severity: "error",
        }),
      );
    }
  };

  return (
    <Box>
      <div style={{ flexGrow: 1 }}>
        <Breadcrumb
          items={breadcrumbItems}
          name={` ${id ? "Edit" : "Add"} Tax Profile`}
        />
      </div>
      <Paper sx={{ p: 3 }} style={{ marginTop: "20px" }}>
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          {/* Main Fields */}
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle1">Basic Information</Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 2, mb: 1, alignItems: "center" }}>
            {/* Tax Profile Name */}
            <Box sx={{ flex: 1 }}>
              <Controller
                name="profileName"
                control={control}
                rules={{
                  required: "Tax Profile name is required",
                  minLength: {
                    value: 3,
                    message: "Tax profile name must be at least 3 characters",
                  },
                  maxLength: {
                    value: 100,
                    message: "Profile name cannot exceed 100 characters",
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Name"
                    onInput={(e: any) => Utils.onlyText(e)}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </Box>

            {/* Tax Profile Code */}
            <Box sx={{ flex: 1 }}>
              <Controller
                name="profileCode"
                control={control}
                rules={{
                  required: "Tax profile code is required",
                  minLength: {
                    value: 3,
                    message: "Tax profile code must be at least 3 characters",
                  },
                  maxLength: {
                    value: 100,
                    message: "Tax profile code cannot exceed 100 characters",
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Code"
                    error={!!error}
                    onInput={(e: any) => Utils.onlyText(e)}
                    helperText={error?.message}
                  />
                )}
              />
            </Box>

            {/* Status Dropdown */}
            <Box sx={{ flex: 1 }}>
              <Controller
                name="isActive"
                control={control}
                rules={{ required: "Status is required" }}
                render={({ field }) => (
                  <FormControl fullWidth size="small" error={!!errors.isActive}>
                    <InputLabel>Status</InputLabel>
                    <Select {...field} label="Status" sx={{ height: 44 }}>
                      <MenuItem value={"true"}>Active</MenuItem>
                      <MenuItem value={"false"}>Inactive</MenuItem>
                    </Select>
                    {errors.isActive && (
                      <FormHelperText>{errors.isActive.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Box>

            {/* Is Default Checkbox */}
            <Box
              sx={{
                flex: 0.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Controller
                name="isDefault"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Is Default"
                  />
                )}
              />
            </Box>
          </Box>

          {/* Tax Details Table */}
          <Box>
            <Typography variant="subtitle1">Tax Details</Typography>

            <TableContainer component={Paper} sx={{ mt: 1 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Country</TableCell>
                    <TableCell>Tax Type</TableCell>
                    <TableCell>Tax Rate (%)</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fields.map((field, index) => {
                    const countryError =
                      errors.taxDetails?.[index]?.countryCode;
                    const taxTypeError = errors.taxDetails?.[index]?.taxType;
                    const taxRateError = errors.taxDetails?.[index]?.taxRate;

                    return (
                      <TableRow key={field.id}>
                        {/* Country */}
                        <TableCell>
                          <Controller
                            name={`taxDetails.${index}.countryCode`}
                            control={control}
                            rules={{ required: "Country is required" }}
                            render={({ field }) => (
                              <FormControl
                                fullWidth
                                size="small"
                                error={!!countryError}
                              >
                                <InputLabel>Country</InputLabel>
                                <Select
                                  {...field}
                                  label="Country"
                                  sx={{ height: 44 }}
                                  open={dropdownOpen[index] || false}
                                  onOpen={() => {
                                    setDropdownOpen({
                                      ...dropdownOpen,
                                      [index]: true,
                                    });
                                  }}
                                  onClose={() => {
                                    setDropdownOpen({
                                      ...dropdownOpen,
                                      [index]: false,
                                    });
                                    setCountrySearch({
                                      ...countrySearch,
                                      [index]: "",
                                    });
                                  }}
                                  value={field.value || ""}
                                  onChange={(e) => {
                                    setValue(
                                      `taxDetails.${index}.countryCode`,
                                      e.target.value,
                                    );
                                  }}
                                  MenuProps={{
                                    PaperProps: {
                                      style: {
                                        maxHeight: 300,
                                      },
                                    },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      p: 1,
                                      position: "sticky",
                                      top: 0,
                                      backgroundColor: "white",
                                      zIndex: 1,
                                    }}
                                  >
                                    <TextField
                                      fullWidth
                                      size="small"
                                      placeholder="Search country..."
                                      value={countrySearch[index] || ""}
                                      onChange={(e) =>
                                        setCountrySearch({
                                          ...countrySearch,
                                          [index]: e.target.value,
                                        })
                                      }
                                      onClick={(e) => e.stopPropagation()}
                                      onKeyDown={(e) => e.stopPropagation()}
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment
                                            position="start"
                                            sx={{ pl: 2 }}
                                          >
                                            <SearchIcon fontSize="small" />
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  </Box>
                                  <MenuItem value="" disabled>
                                    Select Country
                                  </MenuItem>
                                  {filteredCountries(index).map(
                                    (country: any) => (
                                      <MenuItem
                                        key={country.value}
                                        value={country.value}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setValue(
                                            `taxDetails.${index}.countryCode`,
                                            country.value,
                                          );
                                          setCountrySearch({
                                            ...countrySearch,
                                            [index]: "",
                                          });
                                          setDropdownOpen({
                                            ...dropdownOpen,
                                            [index]: false,
                                          });
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                          }}
                                        >
                                          <img
                                            src={country.extraData.flag}
                                            alt={country.label}
                                            style={{
                                              width: "20px",
                                              height: "15px",
                                            }}
                                          />
                                          {country.label}
                                        </Box>
                                      </MenuItem>
                                    ),
                                  )}
                                </Select>
                                {countryError && (
                                  <FormHelperText>
                                    {countryError.message}
                                  </FormHelperText>
                                )}
                              </FormControl>
                            )}
                          />
                        </TableCell>

                        {/* Tax Type */}
                        <TableCell>
                          <FormControl
                            fullWidth
                            size="small"
                            error={!!taxTypeError}
                          >
                            <InputLabel>Tax Type</InputLabel>
                            <Select
                              {...register(
                                `taxDetails.${index}.taxType` as const,
                                {
                                  required: "Tax type is required",
                                },
                              )}
                              defaultValue={field.taxType}
                            >
                              <MenuItem value="">Select Tax Type</MenuItem>
                              <MenuItem value="VAT">VAT</MenuItem>
                              <MenuItem value="GST">GST</MenuItem>
                              <MenuItem value="TAX">Other</MenuItem>
                            </Select>
                            {taxTypeError && (
                              <FormHelperText>
                                {taxTypeError.message}
                              </FormHelperText>
                            )}
                          </FormControl>
                        </TableCell>

                        {/* Tax Rate */}
                        <TableCell>
                          <TextField
                            fullWidth
                            type="number"
                            size="small"
                            error={!!taxRateError}
                            {...register(
                              `taxDetails.${index}.taxRate` as const,
                              {
                                required: "Tax rate is required",
                                setValueAs: (value) =>
                                  value === "" ? undefined : Number(value),
                              },
                            )}
                            defaultValue={field.taxRate}
                            helperText={taxRateError?.message}
                            inputProps={{ step: 0.01, min: 0, max: 100 }}
                          />
                        </TableCell>

                        {/* Remove */}
                        <TableCell>
                          {fields.length > 1 && (
                            <IconButton onClick={() => remove(index)}>
                              <Typography variant="body2">Remove</Typography>
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button variant="contained" color="primary" onClick={addNewRow}>
                Add Tax Detail
              </Button>
            </Box>
          </Box>

          {taxCoverageError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {taxCoverageError}
            </Alert>
          )}

          <Box
            sx={{
              border: 1,
              borderColor: "grey.300",
              borderRadius: 2,
              boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
              width: "100%",
              mt: 2,
              p: 2,
              maxHeight: 400,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              display="flex"
              flex={1}
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  flexShrink: 0,
                  fontWeight: "bold",
                  color: "black",
                  fontSize: "18px",
                }}
              >
                Select Products
              </Typography>

              <TextField
                placeholder="Search products..."
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ width: 280 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ mr: 0 }}>
                      <FaSearch style={{ color: "#9e9e9e", fontSize: 14 }} />
                    </InputAdornment>
                  ),
                  ...(searchQuery && {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setSearchQuery("")}
                        >
                          <Typography sx={{ fontSize: 14, color: "#999" }}>
                            ✕
                          </Typography>
                        </IconButton>
                      </InputAdornment>
                    ),
                  }),
                }}
              />

              <Typography
                sx={{
                  flexShrink: 0,
                  fontWeight: "bold",
                  color: "grey.600",
                  fontSize: "14px",
                }}
              >
                {selectedProducts.length > 0
                  ? `Selected ${selectedProducts.length} / ${productData?.data?.length || 0} Products`
                  : `${productData?.data?.length || 0} Products`}
              </Typography>
            </Box>

            <Box
              ref={scrollRef}
              sx={{
                overflowY: "auto",
                flex: 1,
                pr: 1,
                maskImage: "linear-gradient(to bottom, black 90%, transparent)",
                scrollbarWidth: "none", // Firefox
                "&::-webkit-scrollbar": {
                  display: "none", // Chrome, Safari
                },
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(5, 1fr)",
                  gap: 1.5,
                  p: 3,
                }}
              >
                {productList.map((product) => {
                  const isSelected = selectedProducts.includes(product.id);
                  return (
                    <Box
                      key={product.id}
                      onClick={() => {
                        if (isSelected) {
                          handleProductDeselect(product.id);
                        } else {
                          handleProductSelect(product.id);
                        }
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        px: 1,
                        py: 1,
                        cursor: "pointer",
                        backgroundColor: isSelected ? "#b7d0f7" : "#fff",
                        transition: "background-color 0.2s",
                        "&:hover": {
                          backgroundColor: isSelected ? "#b7d0f7" : "grey.100",
                        },
                      }}
                    >
                      <Box
                        component="img"
                        src={product.productImage}
                        alt={product.productName}
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: 1,
                          objectFit: "cover",
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: isSelected ? "#fff" : "#000",
                          fontWeight: isSelected ? "bold" : "normal",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "100%", // ensures it truncates inside the grid cell
                        }}
                      >
                        {product.productName.length > 15
                          ? `${product.productName.slice(0, 15)}...`
                          : product.productName}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              {isFetching && (
                <Box
                  sx={{
                    py: 2,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={isLoading}
              startIcon={
                isLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null
              }
            >
              {isLoading ? "Saving..." : "Save Taxation"}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate("/taxation")}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default AddTaxation;
